import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Award, User, Mail, Phone, RefreshCw, Loader2, Radio, UserPlus,
  Send, KeyRound, PowerOff, Power, Copy, X as XIcon, ShieldCheck, AlertCircle,
  History, Eye, EyeOff, Pencil, FileText, Upload, Trash2, Download,
} from "lucide-react";
import { PageShell, PageHeader, SectionCard, EmptyState, ActionButton, SearchBar, StatusBadge } from "@/components/ds";

const STATUS_META = {
  active: { label: "Compte actif", tone: "success" },
  invitation_sent: { label: "Invitation envoyée", tone: "yellow" },
  never_connected: { label: "Jamais connecté", tone: "danger" },
  disabled: { label: "Compte désactivé", tone: "navy" },
  no_account: { label: "Pas de compte", tone: "muted" },
};

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [scores, setScores] = useState([]);
  const [affectations, setAffectations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [showCreds, setShowCreds] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [disabledWarning, setDisabledWarning] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: s }, { data: a }, { data: dw }] = await Promise.all([
        api.get("/users?role=driver"),
        api.get("/driver-scores").catch(() => ({ data: [] })),
        api.get("/logibus/affectations/active").catch(() => ({ data: [] })),
        api.get("/admin/disabled-drivers-with-assignments").catch(() => ({ data: { items: [] } })),
      ]);
      setDrivers(d || []);
      setScores(s || []);
      setAffectations(a || []);
      setDisabledWarning(dw?.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scoreByDriver = useMemo(
    () => Object.fromEntries((scores || []).map((s) => [s.driver_id, s])),
    [scores]
  );
  const affectByDriver = useMemo(
    () => Object.fromEntries((affectations || []).filter((a) => a.driver_id).map((a) => [a.driver_id, a])),
    [affectations]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return drivers.filter((d) => !q || (d.name + " " + (d.email || "") + " " + (d.phone || "")).toLowerCase().includes(q));
  }, [drivers, search]);

  const resendInvite = async (d) => {
    try {
      const { data } = await api.post(`/admin/drivers/${d.id}/resend-invite`);
      const ok = data.email_result?.status === "sent";
      toast.success(ok ? "Invitation renvoyée par email" : "Mot de passe régénéré (email skipped — Resend non configuré)");
      setShowCreds({ driver: d, password: data.temp_password_shown_once, emailStatus: data.email_result?.status, action: "resend" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const resetPassword = async (d) => {
    if (!confirm(`Réinitialiser le mot de passe de ${d.name} ? Un email sera envoyé avec les nouveaux accès.`)) return;
    try {
      const { data } = await api.post(`/admin/drivers/${d.id}/reset-password`);
      const ok = data.email_result?.status === "sent";
      toast.success(ok ? "Mot de passe réinitialisé et email envoyé" : "Mot de passe réinitialisé (email skipped — Resend non configuré)");
      setShowCreds({ driver: d, password: data.temp_password_shown_once, emailStatus: data.email_result?.status, action: "reset" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const toggleActive = async (d) => {
    const next = !d.disabled;
    if (next && !confirm(`Désactiver le compte de ${d.name} ? Le chauffeur ne pourra plus se connecter.`)) return;
    try {
      await api.post(`/admin/drivers/${d.id}/toggle-active`, { disabled: next });
      toast.success(next ? "Compte désactivé" : "Compte réactivé");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <PageShell testId="admin-drivers-page">
      <PageHeader
        eyebrow="Personnel roulant"
        title="Chauffeurs"
        subtitle={`${drivers.length} chauffeurs · ${filtered.length} affichés`}
        actions={
          <>
            <ActionButton variant="primary" size="lg" icon={UserPlus} onClick={() => setCreating(true)} testId="create-driver-btn">
              Créer un chauffeur
            </ActionButton>
            <ActionButton variant="ghost" icon={loading ? null : RefreshCw} loading={loading} onClick={load} testId="refresh-btn">
              Rafraîchir
            </ActionButton>
          </>
        }
      />

      <SectionCard
        tone="navy"
        title="Liste des chauffeurs"
        icon={User}
        className="max-w-6xl"
        actions={
          <SearchBar
            tone="dark"
            dense
            value={search}
            onChange={setSearch}
            placeholder="Filtrer par nom, email, téléphone…"
            className="w-72"
            testId="search-input"
          />
        }
      >
        {disabledWarning.length > 0 && (
          <div className="mb-4 border-2 border-amber-600 bg-amber-50 p-3 flex items-start gap-3" data-testid="disabled-driver-warning">
            <AlertCircle size={20} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-bold text-amber-900">
                ⚠ {disabledWarning.length} chauffeur(s) désactivé(s) encore affecté(s) à des tournées
              </div>
              <ul className="mt-1 space-y-0.5">
                {disabledWarning.map((it) => (
                  <li key={it.driver.id} className="text-amber-800 text-xs" data-testid={`disabled-driver-warn-${it.driver.id}`}>
                    <strong>{it.driver.name || it.driver.email}</strong>
                    {" "}— {it.count} affectation(s) active(s) à remplacer
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[11px] text-amber-700">
                Réaffectez ces tournées via <em>Flotte → Affectations</em> ou <em>Planning Bus</em> pour assurer la continuité du service.
              </div>
            </div>
          </div>
        )}
        {filtered.length === 0 ? (
          <EmptyState icon={User} title="Aucun chauffeur correspondant" description="Ajustez la recherche ou créez un nouveau chauffeur." />
        ) : (
          <div className="divide-y divide-zinc-200">
            {filtered.map((d) => {
              const score = scoreByDriver[d.id];
              const aff = affectByDriver[d.id];
              const sv = score?.global_score;
              const sTone = sv == null ? "text-zinc-400" : sv >= 80 ? "text-emerald-700" : sv >= 60 ? "text-orange-700" : "text-red-700";
              const status = d.account_status || "never_connected";
              const sm = STATUS_META[status] || STATUS_META.never_connected;
              return (
                <div key={d.id} data-testid={`driver-${d.id}`} className={`p-3 flex items-center gap-3 flex-wrap hover:bg-zinc-50 transition-colors ${d.disabled ? "opacity-60" : ""}`}>
                  <div className="w-10 h-10 border border-zinc-900 bg-zinc-50 flex items-center justify-center shrink-0">
                    <User size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm tracking-tight truncate flex items-center gap-2 flex-wrap">
                      <span>{d.name}</span>
                      <StatusBadge tone={sm.tone} label={sm.label} size="sm" testId={`status-${d.id}`} />
                      <LogitrakSyncChip driver={d} />
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate flex items-center gap-3 mt-0.5">
                      <span className="inline-flex items-center gap-1"><Mail size={10} /> {d.email}</span>
                      {d.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {d.phone}</span>}
                      {d.last_login && <span title={`Dernière connexion: ${d.last_login}`}>· Connecté {(d.last_login || "").slice(0, 10)}</span>}
                    </div>
                  </div>
                  <div className="text-xs">
                    {aff ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-emerald-700 bg-emerald-50 text-emerald-800 text-[10px] font-mono uppercase tracking-widest">
                        <Radio size={10} /> {aff.trip?.route_short_name || "Affecté"}
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest">Non affecté</span>
                    )}
                  </div>
                  <div className="text-right shrink-0 min-w-[60px]">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Score</div>
                    <div className={`font-display font-black text-xl tracking-tighter leading-none ${sTone}`}>{sv ?? "—"}</div>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    <ActionButton size="sm" variant="ghost" icon={Pencil} onClick={() => setEditingDriver(d)} title="Modifier le chauffeur" testId={`edit-${d.id}`}>
                      Modifier
                    </ActionButton>
                    <ActionButton size="sm" variant="ghost" icon={Send} onClick={() => resendInvite(d)} disabled={d.disabled} title="Envoyer/renvoyer les accès par email" testId={`resend-${d.id}`}>
                      Envoyer accès
                    </ActionButton>
                    <ActionButton size="sm" variant="ghost" icon={KeyRound} onClick={() => resetPassword(d)} disabled={d.disabled} title="Générer un nouveau mot de passe temporaire" testId={`reset-${d.id}`}>
                      Réinit. MDP
                    </ActionButton>
                    <ActionButton
                      size="sm"
                      variant={d.disabled ? "success" : "danger"}
                      icon={d.disabled ? Power : PowerOff}
                      onClick={() => toggleActive(d)}
                      title={d.disabled ? "Réactiver le compte" : "Désactiver le compte"}
                      testId={`toggle-${d.id}`}
                    >
                      {d.disabled ? "Activer" : "Désactiver"}
                    </ActionButton>
                    <ActionButton size="sm" variant="ghost" icon={History} onClick={() => setAuditFor(d)} title="Historique des actions" testId={`audit-${d.id}`}>
                      <span className="sr-only">Audit</span>
                    </ActionButton>
                    <ActionButton size="sm" variant="ghost" icon={User} to={`/admin/drivers/${d.id}`} title="Fiche chauffeur SAEIV" testId={`view-driver-${d.id}`}>
                      <span className="sr-only">Voir</span>
                    </ActionButton>
                    <ActionButton size="sm" variant="ghost" icon={Award} to="/admin/driver-performance" title="Score chauffeurs" testId={`view-perf-${d.id}`}>
                      <span className="sr-only">Performance</span>
                    </ActionButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {creating && (
        <CreateDriverModal
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setShowCreds({ driver: result.user, password: result.temp_password_shown_once, emailStatus: result.email_result?.status, action: "create" });
            load();
          }}
        />
      )}
      {showCreds && <ShowCredsModal data={showCreds} onClose={() => setShowCreds(null)} />}
      {auditFor && <AuditModal driver={auditFor} onClose={() => setAuditFor(null)} />}
      {editingDriver && (
        <EditDriverModal
          driver={editingDriver}
          onClose={() => setEditingDriver(null)}
          onSaved={() => { setEditingDriver(null); load(); }}
        />
      )}
    </PageShell>
  );
}

function CreateDriverModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", license_number: "", send_email: true });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/drivers", form);
      toast.success(`Compte créé pour ${data.user.name}`);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-driver-modal">
      <form onSubmit={submit} className="bg-white border border-zinc-900 w-full max-w-md shadow-[6px_6px_0_0_#0A0A0B]">
        <div className="border-b border-zinc-900 p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Onboarding chauffeur</div>
            <div className="font-display font-black text-xl tracking-tighter">Créer un chauffeur</div>
          </div>
          <button type="button" onClick={onClose} data-testid="create-close" className="text-zinc-500 hover:text-zinc-900"><XIcon size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Nom complet *</label>
            <input data-testid="driver-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex : Jean Dupont"
              className="mt-1 w-full border border-zinc-900 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Email *</label>
            <input data-testid="driver-email-input" required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="jean.dupont@transport.ch"
              className="mt-1 w-full border border-zinc-900 px-3 py-2 text-sm font-mono" />
            <div className="text-[10px] text-zinc-500 mt-1">Servira d'identifiant de connexion</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Téléphone</label>
              <input data-testid="driver-phone-input" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+41 79 …"
                className="mt-1 w-full border border-zinc-900 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">N° permis</label>
              <input data-testid="driver-license-input" value={form.license_number}
                onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                placeholder="(optionnel)"
                className="mt-1 w-full border border-zinc-900 px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm pt-2 border-t border-zinc-200">
            <input type="checkbox" data-testid="send-email-checkbox" checked={form.send_email}
              onChange={(e) => setForm({ ...form, send_email: e.target.checked })} />
            Envoyer automatiquement les accès par email
          </label>
          <div className="text-[10px] font-mono text-zinc-500 bg-amber-50 border border-amber-200 p-2">
            ⓘ Un mot de passe temporaire sécurisé est généré automatiquement. Le chauffeur sera invité à le changer à sa première connexion.
          </div>
        </div>
        <div className="border-t border-zinc-900 p-4 flex items-center justify-end gap-2">
          <ActionButton type="button" variant="ghost" onClick={onClose}>Annuler</ActionButton>
          <ActionButton type="submit" variant="success" icon={submitting ? null : UserPlus} loading={submitting} testId="create-submit">
            Créer le compte
          </ActionButton>
        </div>
      </form>
    </div>
  );
}

function ShowCredsModal({ data, onClose }) {
  const [revealed, setRevealed] = useState(true);
  const { driver, password, emailStatus, action } = data;
  const emailOk = emailStatus === "sent";
  const titles = { create: "Compte créé", resend: "Invitation renvoyée", reset: "Mot de passe réinitialisé" };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copié dans le presse-papier"); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="show-creds-modal">
      <div className="bg-white border border-zinc-900 w-full max-w-md shadow-[6px_6px_0_0_#0A0A0B]">
        <div className="border-b border-zinc-900 p-4">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-emerald-700">
            <ShieldCheck size={11} className="inline mr-1" /> {titles[action]}
          </div>
          <div className="font-display font-black text-xl tracking-tighter">{driver.name}</div>
        </div>
        <div className="p-4 space-y-3">
          {emailOk ? (
            <div className="flex items-start gap-2 p-3 border border-emerald-300 bg-emerald-50 text-sm">
              <Mail size={14} className="text-emerald-700 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-800">Email envoyé à {driver.email}</div>
                <div className="text-[11px] text-emerald-700">Le chauffeur a reçu ses accès. Vous pouvez tout de même les copier ci-dessous pour les transmettre autrement.</div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-3 border border-amber-300 bg-amber-50 text-sm">
              <AlertCircle size={14} className="text-amber-700 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-800">Email non envoyé</div>
                <div className="text-[11px] text-amber-700">Resend n'est pas encore configuré. <strong>Transmettez les accès manuellement</strong> au chauffeur.</div>
              </div>
            </div>
          )}

          <div className="border border-zinc-900">
            <div className="flex items-center justify-between p-2 border-b border-zinc-200 bg-zinc-50">
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Email</div>
                <div className="font-mono text-sm">{driver.email}</div>
              </div>
              <button onClick={() => copy(driver.email)} className="p-1 border border-zinc-900 hover:bg-zinc-900 hover:text-white"><Copy size={11} /></button>
            </div>
            <div className="flex items-center justify-between p-2 bg-[#FFF7D6]">
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-700">Mot de passe temporaire (à usage unique)</div>
                <div className="font-mono text-sm font-bold tracking-widest" data-testid="creds-password">
                  {revealed ? password : "•".repeat(password.length)}
                </div>
              </div>
              <button onClick={() => setRevealed(!revealed)} className="p-1 mr-1 border border-zinc-900 hover:bg-zinc-900 hover:text-white" data-testid="reveal-toggle">
                {revealed ? <EyeOff size={11} /> : <Eye size={11} />}
              </button>
              <button onClick={() => copy(password)} className="p-1 border border-zinc-900 hover:bg-zinc-900 hover:text-white" data-testid="copy-password"><Copy size={11} /></button>
            </div>
          </div>

          <div className="text-[10px] font-mono text-zinc-500">
            🔒 Ce mot de passe ne sera plus jamais affiché. Le chauffeur sera invité à le modifier à sa première connexion.
          </div>
        </div>
        <div className="border-t border-zinc-900 p-4 flex items-center justify-end">
          <ActionButton variant="dark" onClick={onClose} testId="creds-close">Fermer</ActionButton>
        </div>
      </div>
    </div>
  );
}

function AuditModal({ driver, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/drivers/${driver.id}/audit-log`).then(({ data }) => setLogs(data)).catch(() => setLogs([])).finally(() => setLoading(false));
  }, [driver.id]);
  const LABEL = {
    create_account: "Compte créé",
    resend_invite: "Invitation renvoyée",
    reset_password: "Mot de passe réinitialisé",
    disable: "Compte désactivé",
    enable: "Compte réactivé",
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="audit-modal">
      <div className="bg-white border border-zinc-900 w-full max-w-md max-h-[80vh] flex flex-col shadow-[6px_6px_0_0_#0A0A0B]">
        <div className="border-b border-zinc-900 p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Audit log</div>
            <div className="font-display font-black text-lg tracking-tighter">{driver.name}</div>
          </div>
          <button onClick={onClose} data-testid="audit-close" className="text-zinc-500 hover:text-zinc-900"><XIcon size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center text-sm text-zinc-500 py-8"><Loader2 size={16} className="animate-spin inline" /></div>
          ) : logs.length === 0 ? (
            <EmptyState icon={History} title="Aucune action enregistrée" description="L'historique apparaîtra dès la première opération sur ce compte." />
          ) : (
            <ul className="space-y-2">
              {logs.map((l) => (
                <li key={l.id} className="border border-zinc-200 p-2 text-xs">
                  <div className="font-bold">{LABEL[l.action] || l.action}</div>
                  <div className="font-mono text-[10px] text-zinc-500">{(l.created_at || "").slice(0, 19).replace("T", " ")}</div>
                  {l.payload?.email_status && (
                    <div className="text-[10px] text-zinc-600">Email : <strong>{l.payload.email_status}</strong></div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function LogitrakSyncChip({ driver: d }) {
  if (!d?.navixy_employee_id) return null;
  const status = d.navixy_sync_status || "synced";
  const tone = {
    synced:   { bg: "bg-emerald-50", border: "border-emerald-700", text: "text-emerald-800", label: "✓ Logitrak" },
    pending:  { bg: "bg-amber-50",   border: "border-amber-700",   text: "text-amber-800",   label: "⟳ Sync…" },
    failed:   { bg: "bg-red-50",     border: "border-red-700",     text: "text-red-800",     label: "✕ Erreur" },
    conflict: { bg: "bg-orange-50",  border: "border-orange-700",  text: "text-orange-800",  label: "⚠ Conflit" },
  }[status] || { bg: "bg-zinc-50", border: "border-zinc-400", text: "text-zinc-700", label: status };
  return (
    <span
      title={d.last_push_error ? `Dernière erreur: ${d.last_push_error}` : `Synchronisé avec Logitrak (#${d.navixy_employee_id})`}
      data-testid={`navixy-chip-${d.id}`}
      className={`inline-flex items-center px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-widest ${tone.bg} ${tone.border} ${tone.text}`}
    >
      {tone.label}
    </span>
  );
}

/**
 * Edit driver form.
 *  - Editable fields: first_name, last_name, email, phone, license, personnel #
 *  - Read-only: navixy_employee_id, navixy_tracker_id, role
 *  - When the driver is linked to Logitrak, every saved change is auto-pushed
 *    to Logitrak in the background and the sync chip turns "pending" then "synced".
 */
function EditDriverModal({ driver, onClose, onSaved }) {
  const linked = !!driver.navixy_employee_id;
  const [form, setForm] = useState({
    first_name: driver.first_name || "",
    last_name: driver.last_name || "",
    email: driver.email && driver.email.endsWith("@logibus.local") ? "" : (driver.email || ""),
    phone: driver.phone || "",
    driver_license_number: driver.driver_license_number || "",
    personnel_number: driver.personnel_number || "",
    address: driver.address || "",
    postal_code: driver.postal_code || "",
    city: driver.city || "",
    country: driver.country || "",
    date_of_birth: (driver.date_of_birth || "").slice(0, 10),
    license_category: driver.license_category || "",
    license_expiry: (driver.license_expiry || "").slice(0, 10),
    hire_date: (driver.hire_date || "").slice(0, 10),
    emergency_contact_name: driver.emergency_contact_name || "",
    emergency_contact_phone: driver.emergency_contact_phone || "",
    notes: driver.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        const original = driver[k] || "";
        const cur = (k === "date_of_birth" || k === "license_expiry" || k === "hire_date")
          ? (original || "").slice(0, 10)
          : original;
        if (v !== cur) payload[k] = v;
      });
      if (!Object.keys(payload).length) { onClose(); return; }
      const { data } = await api.patch(`/admin/drivers/${driver.id}`, payload);
      if (data.pushed) {
        toast.success("Chauffeur modifié — synchronisation Logitrak en cours…");
      } else {
        toast.success("Chauffeur modifié");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="edit-driver-modal">
      <div className="bg-white border border-zinc-900 max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <header className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between bg-[#FFC60A] sticky top-0 z-10">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase">Modifier · Chauffeur</div>
            <h2 className="font-display font-black text-2xl tracking-tighter mt-0.5">{driver.name}</h2>
          </div>
          <button onClick={onClose} aria-label="Fermer" data-testid="edit-driver-close"
            className="w-9 h-9 border border-zinc-900 bg-white hover:bg-zinc-100 flex items-center justify-center">
            <XIcon size={14} />
          </button>
        </header>
        <div className="p-5 space-y-5">
          {linked && (
            <div className="border border-emerald-700 bg-emerald-50 p-3 flex items-start gap-2" data-testid="edit-driver-navixy-notice">
              <ShieldCheck size={16} className="text-emerald-700 mt-0.5" />
              <div className="text-xs">
                <div className="font-bold">Synchronisation Logitrak automatique</div>
                <div className="text-[11px] mt-0.5">
                  Ce chauffeur est lié à l'employé Logitrak <strong>#{driver.navixy_employee_id}</strong>. Les champs Prénom / Nom / Email / Téléphone / N° permis seront poussés vers Logitrak en arrière-plan.
                </div>
              </div>
            </div>
          )}

          <FormSection title="Identité">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Prénom" value={form.first_name}
                onChange={(v) => setField("first_name", v)} testId="edit-first-name" />
              <Field label="Nom" value={form.last_name}
                onChange={(v) => setField("last_name", v)} testId="edit-last-name" />
              <Field label="Date de naissance" value={form.date_of_birth} type="date"
                onChange={(v) => setField("date_of_birth", v)} testId="edit-dob" />
              <Field label="N° personnel" value={form.personnel_number}
                onChange={(v) => setField("personnel_number", v)} testId="edit-personnel" />
            </div>
          </FormSection>

          <FormSection title="Contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Email" value={form.email} type="email"
                placeholder={driver.email?.endsWith("@logibus.local") ? "(synthétique — saisir le vrai email)" : ""}
                onChange={(v) => setField("email", v)} testId="edit-email" />
              <Field label="Téléphone" value={form.phone}
                onChange={(v) => setField("phone", v)} testId="edit-phone" />
            </div>
          </FormSection>

          <FormSection title="Adresse">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Adresse (rue, n°)" value={form.address}
                onChange={(v) => setField("address", v)} testId="edit-address"
                wide />
              <Field label="Code postal" value={form.postal_code}
                onChange={(v) => setField("postal_code", v)} testId="edit-postal" />
              <Field label="Ville" value={form.city}
                onChange={(v) => setField("city", v)} testId="edit-city" />
              <Field label="Pays" value={form.country}
                onChange={(v) => setField("country", v)} testId="edit-country"
                placeholder="Suisse" />
            </div>
          </FormSection>

          <FormSection title="Permis de conduire">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="N° permis" value={form.driver_license_number}
                onChange={(v) => setField("driver_license_number", v)} testId="edit-license" />
              <Field label="Catégorie" value={form.license_category}
                onChange={(v) => setField("license_category", v)} testId="edit-license-cat"
                placeholder="D, D1, D1E…" />
              <Field label="Date d'expiration" value={form.license_expiry} type="date"
                onChange={(v) => setField("license_expiry", v)} testId="edit-license-exp" />
            </div>
          </FormSection>

          <FormSection title="RH & Urgence">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Date d'embauche" value={form.hire_date} type="date"
                onChange={(v) => setField("hire_date", v)} testId="edit-hire-date" />
              <div className="hidden sm:block" />
              <Field label="Contact d'urgence — Nom" value={form.emergency_contact_name}
                onChange={(v) => setField("emergency_contact_name", v)} testId="edit-emerg-name" />
              <Field label="Contact d'urgence — Téléphone" value={form.emergency_contact_phone}
                onChange={(v) => setField("emergency_contact_phone", v)} testId="edit-emerg-phone" />
            </div>
            <label className="block mt-3">
              <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Notes internes</span>
              <textarea
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                rows={3}
                data-testid="edit-notes"
                placeholder="Remarques, restrictions, formations, etc."
                className="mt-1 w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white focus:outline-none focus:bg-[#FFFBE6]"
              />
            </label>
          </FormSection>

          <DriverDocumentsSection driverId={driver.id} />

          {linked && (
            <FormSection title="Logitrak (lecture seule)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ReadOnly label="ID Logitrak" value={`#${driver.navixy_employee_id}`} />
                <ReadOnly label="Tracker Logitrak" value={driver.navixy_tracker_id || "—"} />
                <ReadOnly label="Statut sync" value={driver.navixy_sync_status || "synced"} />
                <ReadOnly label="Dernier push" value={driver.last_pushed_to_navixy_at ? new Date(driver.last_pushed_to_navixy_at).toLocaleString("fr-CH") : "—"} />
                {driver.last_push_error && (
                  <div className="sm:col-span-2 border border-red-700 bg-red-50 p-2 text-[11px] font-mono text-red-800" data-testid="edit-driver-last-error">
                    Dernière erreur Logitrak : {driver.last_push_error}
                  </div>
                )}
              </div>
            </FormSection>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-900 flex justify-end gap-2 bg-zinc-50 sticky bottom-0">
          <button onClick={onClose} className="border border-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100"
            data-testid="edit-driver-cancel">
            Annuler
          </button>
          <button onClick={save} disabled={saving}
            className="border border-zinc-900 bg-zinc-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="edit-driver-save">
            {saving && <Loader2 size={12} className="animate-spin" />} Enregistrer
          </button>
        </footer>
      </div>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <section className="space-y-2">
      <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-200 pb-1">{title}</div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", testId, wide = false }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</span>
      <input
        type={type}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white focus:outline-none focus:bg-[#FFFBE6]"
      />
    </label>
  );
}

function ReadOnly({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</div>
      <div className="font-mono text-xs mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200">{value || "—"}</div>
    </div>
  );
}

const DOC_CATEGORIES = [
  { value: "license",  label: "Permis de conduire" },
  { value: "id_card",  label: "Carte d'identité" },
  { value: "medical",  label: "Visite médicale" },
  { value: "contract", label: "Contrat" },
  { value: "training", label: "Formation (OACP…)" },
  { value: "other",    label: "Autre" },
];

const DOC_CATEGORY_LABEL = Object.fromEntries(DOC_CATEGORIES.map((c) => [c.value, c.label]));

function DriverDocumentsSection({ driverId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("license");
  const [labelTxt, setLabelTxt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/drivers/${driverId}/documents`);
      setDocs(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [driverId]);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5 Mo).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (labelTxt) fd.append("label", labelTxt);
      if (expiresAt) fd.append("expires_at", expiresAt);
      await api.post(`/admin/drivers/${driverId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document téléversé");
      setLabelTxt(""); setExpiresAt("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = (d) => {
    const url = `${api.defaults.baseURL || ""}/attachments/${d.id}/download`;
    window.open(url, "_blank");
  };

  const remove = async (d) => {
    if (!confirm(`Supprimer "${d.file_name}" ?`)) return;
    try {
      await api.delete(`/admin/drivers/${driverId}/documents/${d.id}`);
      toast.success("Document supprimé");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} Ko` : `${(b / 1024 / 1024).toFixed(1)} Mo`;

  return (
    <FormSection title="Documents (PDF / image)">
      <div className="border border-zinc-900 bg-zinc-50 p-3 space-y-2" data-testid="driver-docs-uploader">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="block">
            <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-zinc-500">Type</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="doc-category"
              className="mt-1 w-full border border-zinc-900 px-2 py-2 text-xs font-mono bg-white"
            >
              {DOC_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="block sm:col-span-1">
            <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-zinc-500">Libellé (optionnel)</span>
            <input
              value={labelTxt}
              onChange={(e) => setLabelTxt(e.target.value)}
              placeholder="Ex : Permis cat. D"
              data-testid="doc-label"
              className="mt-1 w-full border border-zinc-900 px-2 py-2 text-xs font-mono bg-white"
            />
          </label>
          <label className="block">
            <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-zinc-500">Date d'expiration</span>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              data-testid="doc-expires"
              className="mt-1 w-full border border-zinc-900 px-2 py-2 text-xs font-mono bg-white"
            />
          </label>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={handleFile}
          className="hidden"
          data-testid="doc-file-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="doc-upload-btn"
          className="w-full border border-zinc-900 bg-zinc-900 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? "Téléversement…" : "Sélectionner un fichier (PDF, JPG, PNG — max 5 Mo)"}
        </button>
      </div>

      <div className="mt-3 border border-zinc-200" data-testid="driver-docs-list">
        {loading ? (
          <div className="p-4 text-xs text-zinc-500 text-center"><Loader2 size={12} className="inline animate-spin mr-1" /> Chargement…</div>
        ) : docs.length === 0 ? (
          <div className="p-4 text-xs text-zinc-500 text-center">Aucun document enregistré.</div>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {docs.map((d) => {
              const exp = d.expires_at ? new Date(d.expires_at) : null;
              const now = new Date();
              const days = exp ? Math.round((exp - now) / (1000 * 60 * 60 * 24)) : null;
              const expSoon = days !== null && days >= 0 && days <= 30;
              const expired = days !== null && days < 0;
              return (
                <li key={d.id} data-testid={`doc-row-${d.id}`} className="p-2 flex items-center gap-3 hover:bg-zinc-50">
                  <div className="w-8 h-8 border border-zinc-900 bg-white flex items-center justify-center shrink-0">
                    <FileText size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{d.label || d.file_name}</div>
                    <div className="text-[10px] font-mono text-zinc-500 flex flex-wrap items-center gap-2 mt-0.5">
                      <span className="px-1.5 py-0.5 border border-zinc-700 bg-white uppercase tracking-widest text-[9px]">
                        {DOC_CATEGORY_LABEL[d.category] || d.category || "Autre"}
                      </span>
                      <span>{d.file_name}</span>
                      <span>· {fmtSize(d.size_bytes)}</span>
                      <span>· {(d.uploaded_at || "").slice(0, 10)}</span>
                      {exp && (
                        <span className={expired ? "text-red-700 font-bold" : expSoon ? "text-orange-700 font-bold" : "text-zinc-600"}>
                          · Expire {(d.expires_at || "").slice(0, 10)}
                          {expired && " ⚠ EXPIRÉ"}
                          {expSoon && !expired && ` ⚠ ${days}j`}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => download(d)}
                    title="Télécharger / Ouvrir"
                    data-testid={`doc-download-${d.id}`}
                    className="p-1.5 border border-zinc-900 bg-white hover:bg-zinc-100"
                  >
                    <Download size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(d)}
                    title="Supprimer"
                    data-testid={`doc-delete-${d.id}`}
                    className="p-1.5 border border-red-700 bg-white text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </FormSection>
  );
}
